import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { Link } from 'react-router-dom';
import { usePlaylist } from '../../../contexts/PlaylistContext';
import useWindowSize from '../../../hooks/useWindowSize';
import { deriveHomepageBio } from '../../../utils/bioUtils';
import { BREAKPOINT, getArtistPath, getOptimizedImageUrl, safeString, trackClick } from '../../_helpers';
import { AudioSpectrogram } from '../../ui/AudioSpectrogram';
import { heroProps } from '../../../utils/sharedElements';

export function HeroSection(props) {
  const windowSize = useWindowSize();
  const { artistData, song } = props;
  const { isPlaying, togglePlayPause, playlist } = usePlaylist();
  const artistName = artistData.name;
  const artistImage = artistData?.images?.[0]?.url || '';
  const trackData = song?.trackData;
  const [showHalfBioMobile, setShowHalfBioMobile] = React.useState(false);
  const [isHovering, setIsHovering] = React.useState(false);

  const hasTracksLoaded = playlist.length > 0;

  const handleProfileClick = () => {
    trackClick(hasTracksLoaded && isPlaying ? 'hero-profile-pause' : 'hero-profile-play', {
      label: hasTracksLoaded ? 'Artist profile play toggle' : 'Artist profile (no tracks)',
      artist: artistName,
      section: 'hero',
      trackId: song?.id,
      track: song?.title,
    });
    if (hasTracksLoaded) {
      togglePlayPause();
    }
  };

  const displayArtistBio = deriveHomepageBio(
    artistName,
    artistData,
    trackData?.albums || [],
    windowSize.width < BREAKPOINT && !showHalfBioMobile
  );

  const fullBioLink = artistName ? (
    <Link to={getArtistPath(artistName, '/bio')}>
      <Box
        className="Full-bio-link"
        sx={{
          display: 'inline-flex',
          gap: '.5rem',
          position: 'relative',
          color: 'var(--logoColor)',
          fontSize: { xs: '2.1rem', md: '1.2rem' },
          fontFamily: 'Poppins',
          fontWeight: '700',
          lineHeight: '1.9',
          cursor: 'pointer',
          textDecoration: 'none',
          ml: { xs: '0.65rem', md: '0.4rem' },
          '&:before': {
            content: '""',
            position: 'absolute',
            bottom: '0.22rem',
            left: '0',
            width: '100%',
            height: '1px',
            backgroundColor: 'var(--color1)',
          },
        }}
      >
        Full Bio
        <span className="MoreArrow">
          <svg
            width="0.625rem"
            height="0.8125rem"
            viewBox="0 0 10 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.6598 4.6471C10.0543 5.54694 10.0543 7.58645 8.6598 8.48628L3.53363 11.7941C2.01344 12.775 0.0104383 11.6837 0.0104384 9.87447L0.0104387 3.25891C0.0104388 1.44971 2.01344 0.358382 3.53364 1.33932L8.6598 4.6471Z"
              fill="var(--logoColor)"
            />
          </svg>
        </span>
      </Box>
    </Link>
  ) : null;

  return (
    <Box
      sx={{
        mt: { xs: '105px', md: '9.1rem' },
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '3.0625rem',
        display: 'inline-flex',
        width: '100%',
      }}
      className="HeroSection"
    >
      <motion.div layoutId="artist-avatar" transition={{ layout: { duration: 0.4 } }}>
        <Box
          className="StoryIcon artist-profile-play"
          onClick={handleProfileClick}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          data-playing={isPlaying}
          data-has-tracks={hasTracksLoaded}
          sx={{
            width: { xs: '28rem', md: '12.25rem' },
            height: { xs: '28rem', md: '12.25rem' },
            position: 'relative',
            mt: '2rem',
            mb: { xs: '7rem', md: '2rem' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: hasTracksLoaded ? 'pointer' : 'default',
          }}
        >
          <AudioSpectrogram
            size={windowSize.width < BREAKPOINT ? '41rem' : '17rem'}
            strokeWidth={windowSize.width < BREAKPOINT ? '0.65rem' : '0.3125rem'}
            className="audio-spectrogram"
          />
          <Box
            component="img"
            {...heroProps('avatar')}
            src={getOptimizedImageUrl(artistImage, {
              width: 448,
              height: 448,
              fit: 'cover',
              quality: 85,
            })}
            alt={`${artistName} profile picture`}
            sx={{
              width: { xs: '26.3rem', md: '11rem' },
              height: { xs: '26.3rem', md: '11rem' },
              borderRadius: '28rem',
              objectFit: 'cover',
              transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
              transform: isHovering && hasTracksLoaded ? 'scale(1.02)' : 'scale(1)',
            }}
            className="Image1"
          />
          {hasTracksLoaded && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <motion.div
                key={isPlaying ? 'pause' : 'play'}
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: isHovering ? 0.25 : 0 }}
                transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                style={{
                  width: windowSize.width < BREAKPOINT ? '10rem' : '4.5rem',
                  height: windowSize.width < BREAKPOINT ? '10rem' : '4.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isPlaying ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="var(--logoColor)"
                    style={{ width: '100%', height: '100%' }}
                  >
                    <rect x="5" y="4" width="5" height="16" rx="1.5" />
                    <rect x="14" y="4" width="5" height="16" rx="1.5" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="var(--logoColor)"
                    style={{ width: '100%', height: '100%' }}
                  >
                    <path d="M6.5 4.8c0-1.1 1.2-1.8 2.1-1.2l11.1 7.2c.85.55.85 1.85 0 2.4L8.6 20.4c-.9.6-2.1-.1-2.1-1.2V4.8z" />
                  </svg>
                )}
              </motion.div>
            </Box>
          )}
          <Box
            className="Ellipse10"
            sx={{
              width: { xs: '29rem', md: '12.25rem' },
              height: { xs: '29rem', md: '12.25rem' },
              position: 'absolute',
              borderRadius: '28rem',
              pointerEvents: 'none',
            }}
          />
        </Box>
      </motion.div>

      <Box
        className={safeString(artistName)}
        sx={{
          color: 'var(--logoColor)',
          fontFamily: 'Bellefair',
          fontSize: { xs: '5.6rem', md: '7rem', lg: '8.75rem' },
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: 'normal',
          letterSpacing: '-0.15rem',
          textTransform: 'uppercase',
        }}
      >
        {artistName}
      </Box>

      {!artistData ? (
        <Box
          sx={{
            width: '77%',
            borderRadius: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.16rem',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '20px',
              backgroundColor: '#ffffff10',
              borderRadius: '0.5rem',
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '20px',
              backgroundColor: '#ffffff10',
              borderRadius: '0.5rem',
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: '20px',
              backgroundColor: '#ffffff10',
              borderRadius: '0.5rem',
            }}
          />
          {fullBioLink}
        </Box>
      ) : (
        <Box
          className="IntroBio"
          sx={{
            width: '100%',
            maxWidth: '60rem',
            padding: { xs: '0 5vw', md: '0' },
            textAlign: 'center',
            color: 'var(--logoColor)',
            fontSize: { xs: '2.1rem', md: '1.2rem' },
            fontFamily: 'Poppins',
            fontWeight: '400',
            lineHeight: '1.9',
            overflowWrap: 'break-word',
            animation: 'fade-in 1s ease',
            '& > span': {
              minHeight: { xs: showHalfBioMobile ? '238px' : '147px', md: 'auto' },
              transition: 'min-height 0.5s ease',
            },
          }}
        >
          {displayArtistBio}{' '}
          {/* {windowSize.width < BREAKPOINT && (
            <Box
              className="More-btn"
              onClick={() => {
                setShowHalfBioMobile(!showHalfBioMobile);
              }}
              sx={{
                display: 'inline-flex',
                gap: '.65rem',
                position: 'relative',
                color: 'var(--logoColor)',
                fontSize: 'inherit',
                fontFamily: 'Poppins',
                fontWeight: '700',
                lineHeight: 'inherit',
                marginLeft: '1rem',
                cursor: 'pointer',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  bottom: '3px',
                  left: '0',
                  width: showHalfBioMobile ? '92%' : '82%',
                  height: '2px',
                  backgroundColor: 'var(--color1)',
                },
              }}
            >
              {showHalfBioMobile ? 'Collapse Bio ' : 'More '}
              <span className="MoreArrow">
                <svg
                  width="0.625rem"
                  height="0.8125rem"
                  viewBox="0 0 10 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.6598 4.6471C10.0543 5.54694 10.0543 7.58645 8.6598 8.48628L3.53363 11.7941C2.01344 12.775 0.0104383 11.6837 0.0104384 9.87447L0.0104387 3.25891C0.0104388 1.44971 2.01344 0.358382 3.53364 1.33932L8.6598 4.6471Z"
                    fill="var(--logoColor)"
                  />
                </svg>
              </span>
            </Box>
          )} */}
          {fullBioLink}
        </Box>
      )}

      <Box
        className="Scroll"
        sx={{
          color: 'var(--logoColor)',
          fontSize: { xs: '2.75rem', md: '1.75rem' },
          fontFamily: 'Bellefair',
          fontWeight: '400',
          textTransform: 'uppercase',
          mt: { xs: '17rem', md: '7rem' },
          animation: 'float 2.8s cubic-bezier(0.07, 0.41, 0.55, 0.98) infinite',
        }}
      >
        SCROLL
      </Box>
    </Box>
  );
}
